'use client';

import { useEffect, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { serverNow, isAvatarAlive } from './api';
import type { Avatar, FeedAvatar, Session } from './api';
import { processMyAvatar } from './queries/useAvatars';

/**
 * Single 1Hz heartbeat for the whole app:
 *
 *  1. **Cache reaper.** Walks the TanStack caches that carry an avatar
 *     deadline (`['avatar','me']`, `['feed']`, `['sessions','mine']`,
 *     every `['sessions', id]`) and stamps `expiredAt` locally on
 *     entries whose natural deadline has passed. TanStack notifies the
 *     subscribed components, so views update without each one running
 *     its own timer. This is the FE-side mirror of the BE 1s reaper:
 *     liveness flips at most ~1s after the deadline even when the
 *     network is dead.
 *
 *  2. **Subscriber registry.** Views that need to re-render every
 *     second (countdown pills, "Nm ago" labels, etc.) register a
 *     callback via `useGlobalTick()` and get a `now` value back per
 *     tick. No new `setInterval` per view.
 *
 * The tick is started once from `<AppLayout>`'s effect; stopping is
 * idempotent.
 */

type TickCb = (now: number) => void;
const subscribers = new Set<TickCb>();

let timer: ReturnType<typeof setInterval> | null = null;
let qcRef: QueryClient | null = null;

export function startGlobalTick(qc: QueryClient) {
  if (timer) return;
  qcRef = qc;
  timer = setInterval(tick, 1000);
}

export function stopGlobalTick() {
  if (timer) clearInterval(timer);
  timer = null;
  qcRef = null;
}

function tick() {
  const now = serverNow();
  if (qcRef) reapCaches(qcRef, now);
  for (const cb of subscribers) {
    try {
      cb(now);
    } catch (e) {
      console.error('globalTick subscriber threw', e);
    }
  }
}

/**
 * Re-renders the calling component every second and returns the
 * current `serverNow()`. The component can use the value or just
 * ignore it and read whatever it needs (`serverNow()`,
 * `isAvatarAlive(...)`, …) on each tick.
 */
export function useGlobalTick(): number {
  const [now, setNow] = useState(serverNow);
  useEffect(() => {
    const cb: TickCb = (n) => setNow(n);
    subscribers.add(cb);
    return () => {
      subscribers.delete(cb);
    };
  }, []);
  return now;
}

// ── Reaper ────────────────────────────────────────────────────────────

function deadlineOf(a: { createdAt: number; duration: number }): number {
  return a.createdAt + a.duration * 1000;
}

type SessionsPage = { items: Session[]; next: string | null; prev: string | null };
type SessionsCache = { pages: SessionsPage[]; pageParams: unknown[] };

function reapCaches(qc: QueryClient, now: number) {
  // 1) ['avatar','me'] — stamp expiredAt and let processMyAvatar route
  //    to history and clear the slot.
  const me = qc.getQueryData<Avatar | null>(['avatar', 'me']);
  if (me && !me.expiredAt && !me.deletedAt && deadlineOf(me) <= now) {
    processMyAvatar(qc, { ...me, expiredAt: deadlineOf(me) });
  }

  // 2) ['feed'] — drop entries past their deadline. Cache shape is the
  //    paginated envelope `{ pages: { items, next, prev }[] }`; reap
  //    inside `items`.
  type FeedPage = { items: FeedAvatar[]; next: string | null; prev: string | null };
  qc.setQueryData<{ pages: FeedPage[]; pageParams?: unknown[] } | undefined>(
    ['feed'],
    (old) => {
      if (!old) return old;
      let changed = false;
      const pages = old.pages.map((p) => {
        const items = p.items.filter((a) => isAvatarAlive(a));
        if (items.length === p.items.length) return p;
        changed = true;
        return { ...p, items };
      });
      return changed ? { ...old, pages } : old;
    },
  );

  // 3) ['sessions','mine','seeker'] and ['sessions','mine','avatar']
  //    — stamp session.expiredAt on rows whose embedded avatar's
  //    deadline passed. Same value the BE writes (the avatar's natural
  //    deadline), so when its UPDATE lands a few hundred ms later it's
  //    a no-op.
  const stamp = (s: Session): Session => {
    if (s.expiredAt != null) return s;
    const a = s.avatar;
    if (!a) return s;
    const d = deadlineOf(a);
    if (d > now) return s;
    // Mirror the BE clamp on natural expire (reapDead): per-side
    // lastReadAt = lastMessageAt. Without this, the cache keeps the
    // unread dot stuck on a now-expired session and the /messages
    // tab badge flips off as soon as the row drops past page 1.
    return { ...s, expiredAt: d, lastReadAt: s.lastMessageAt };
  };
  const stampPages = (old: SessionsCache | undefined): SessionsCache | undefined => {
    if (!old?.pages.length) return old;
    let changed = false;
    const pages = old.pages.map(p => {
      const items = p.items.map(stamp);
      if (items.some((s, i) => s !== p.items[i])) {
        changed = true;
        return { ...p, items };
      }
      return p;
    });
    return changed ? { ...old, pages } : old;
  };
  qc.setQueryData<SessionsCache>(['sessions', 'mine', 'seeker'], stampPages);
  qc.setQueryData<SessionsCache>(['sessions', 'mine', 'avatar'], stampPages);

  // 4) ['sessions', id] — every per-id session cache entry.
  const cache = qc.getQueryCache();
  const sessionQueries = cache.findAll({
    predicate: (q) => {
      const k = q.queryKey;
      return (
        Array.isArray(k) &&
        k.length === 2 &&
        k[0] === 'sessions' &&
        typeof k[1] === 'string' &&
        k[1] !== 'mine' &&
        k[1] !== 'open'
      );
    },
  });
  for (const q of sessionQueries) {
    const data = q.state.data as Session | undefined;
    if (!data || data.expiredAt != null) continue;
    const a = data.avatar;
    if (!a) continue;
    const d = deadlineOf(a);
    if (d > now) continue;
    qc.setQueryData<Session>(q.queryKey, {
      ...data,
      expiredAt: d,
      lastReadAt: data.lastMessageAt,
    });
  }
}
